import React, { Suspense, useMemo } from "react";
import PropTypes from "prop-types";


import {
  AdminLoadingState,
} from "../../pages/admin/ui_engine";
/**
 * GenericStudioAdminPanel
 *
 * Adaptateur générique entre un panneau du framework Admin et un Studio.
 *
 * Le Workspace Admin possède exclusivement l'état fullscreen.
 * Ce composant traduit uniquement le contrat Admin vers le mode
 * de présentation Studio.
 *
 * Le composant ne connaît aucun domaine métier ni aucune StudioDefinition.
 */
export default function GenericStudioAdminPanel({
  StudioComponent = null,
  loadStudioComponent = null,
  studioProps = {},
  className = "generic-studio-admin-panel",
  isFullscreen = false,
}) {
  const panelClassName = [
    className,
    isFullscreen ? `${className}--fullscreen` : "",
  ]
    .filter(Boolean)
    .join(" ");

  const LazyStudioComponent = useMemo(() => {
    if (typeof loadStudioComponent !== "function") {
      return null;
    }

    return React.lazy(async () => {
      const module = await loadStudioComponent();

      if (!module || typeof module !== "object") {
        throw new TypeError(
          "Studio loader must resolve to a module object."
        );
      }

      const Component = module.default;

      if (!Component) {
        throw new TypeError(
          "Studio loader module must expose a default component."
        );
      }

      return {
        default: Component,
      };
    });
  }, [loadStudioComponent]);

  const ResolvedStudioComponent =
    LazyStudioComponent || StudioComponent;

  if (!ResolvedStudioComponent) {
    throw new TypeError(
      "GenericStudioAdminPanel requires StudioComponent or loadStudioComponent."
    );
  }

  return (
    <div className={panelClassName}>
      <Suspense
        fallback={<AdminLoadingState message="Chargement du Studio..." />}
      >
        <ResolvedStudioComponent
          {...studioProps}
          mode={isFullscreen ? "fullscreen" : "embedded"}
        />
      </Suspense>
    </div>
  );
}

GenericStudioAdminPanel.propTypes = {
  StudioComponent: PropTypes.elementType,
  loadStudioComponent: PropTypes.func,
  studioProps: PropTypes.object,
  className: PropTypes.string,
  isFullscreen: PropTypes.bool,
};
